import { computed } from "vue";
import { useOidcAuth } from "~/auth/oidcService";
import { useActorsStore } from "~/stores/actors";
import type { FindMeActor } from "~/api/findMeTypes";

// Single point of truth for who is acting on find-me commands.
//
// AUTHZ PLUMBING NOTE: real role-based auth is not wired yet — the B2C token
// carries no role claim and the backend does not enforce roles. Everything
// that needs actor identity or admin gating reads it from here, so when the
// authz work lands only this composable changes:
//   - resolve `role` from the real token claim (or an API lookup)
//   - drop the permissive isAdmin default below
export const useFindMeActor = () => {
  const { user } = useOidcAuth();

  const profile = computed(
    () => (user.value?.profile ?? null) as Record<string, unknown> | null,
  );

  const claim = (key: string): string | null => {
    const value = profile.value?.[key];
    return typeof value === "string" && value ? value : null;
  };

  // The actor registered for the process currently on screen, when the entry
  // prompt has run. Registration is per incident, so the same staff member
  // working two processes is two different actor ids by design.
  const actorsStore = useActorsStore();
  const registration = computed(() => actorsStore.currentRegistration);

  /**
   * Stable identifier for the audit trail. The per-incident registration wins
   * when there is one; otherwise fall back to the B2C claims this used to read
   * exclusively, so dashboards where the prompt was skipped or the registry is
   * unreachable still attribute their commands to something.
   *
   * ONLY the id goes on commands. The display name is a server-side lookup
   * from this id, which is what makes a corrected name fix every past record
   * instead of only future ones — see api/actorTypes.ts.
   *
   * KNOWN WART, not a bug: this puts two id spaces into one field. The find-me
   * `actors` map resolves registry GUIDs to names, but a B2C claim id is not in
   * that map and so resolves to itself and renders as a raw guid. The effect is
   * backwards — an authenticated user who skipped the prompt displays WORSE
   * than an unauthenticated one who registered. Left as-is deliberately: it
   * resolves when the auth track lands and B2C identity becomes resolvable
   * server-side. Expect it to be reported as a bug before then.
   */
  const actorUserId = computed(
    () =>
      registration.value?.actorUserId ??
      claim("oid") ??
      claim("sub") ??
      claim("email") ??
      claim("name"),
  );

  // For display only — never send this on a command.
  const actorName = computed(
    () => registration.value?.name ?? claim("name") ?? claim("email"),
  );

  // TODO(authz): resolve from the real role claim once it exists. Until then,
  // every dashboard user is treated as Admin so the override actions stay
  // usable — the backend does not enforce roles yet either.
  const isAdmin = computed(() => true);

  // The registered role is what staff actually chose for this incident; the
  // isAdmin fallback below is the pre-registry behaviour and stays until real
  // role claims land.
  const actorRole = computed(
    () => registration.value?.role ?? (isAdmin.value ? "Admin" : "Staff"),
  );

  // Ready-to-spread actor fields for command payloads.
  const actor = computed<FindMeActor>(() => ({
    actorUserId: actorUserId.value,
    actorRole: actorRole.value,
  }));

  return {
    actorUserId,
    actorName,
    actorRole,
    isAdmin,
    actor,
    registration,
  };
};
